import { uploadProjectForConversion } from "../components/ProjectSelection/functions";
import { showToast, showErrToast } from "../components/Toasts";
import { ExportState } from "../models/ProjectMetadata";
import universalstorageHandler from "../persistence/UniversalstorageHandler";
import { cleanEncode } from "../utils/CommonUtils";
import { checkIfEtsOnline, reopenEtsDialog, showETSSyncStatsWithCatalogIssues } from "./etsFunctions";
import { etsPin } from "./etsWebSocket";
import Logger from "../Logger";
import { itx } from "../translationItx/itx";
import axios from "../utils/axios";

const logger = new Logger();
const ETS_PROJECT_SUMMARY_SUFFIX = ".summary.json"; // no itx

const t = (key: string, def: string) => itx(key, def);

export const serverConvertionMessages = {
  offline: t("homePage.etsServerOffline", "ETS Conversion Failed: Server Offline"), // no itx
  timeout: t("homePage.etsConversionTimedOut", "ETS Conversion Timeout"), // no itx
  error: t("homePage.etsConversionError", "ETS Conversion Failed: Conversion Error"), // no itx
  failed: t("homePage.etsConversionFailed", "ETS Conversion Failed"), // no itx
  ready: t("projectCard.etsDownloadReady", "ETS-Project is ready for download"), // no itx
  waiting: t("homePage.WaitingForDownloadExportState", "Warten auf ETS Download"), // no itx
  download: t("projectCard.etsDownload", "Download ETS-Project"), // no itx
  export: t("etsSyncAndExport.exportProjectData", "ETS-Projektdatei exportieren"), // no itx
};

export async function checkIfPendingEtsConversionReady(): Promise<string> {
  try {
    const res = await axios.get(`/api/projectexport/checkProjectData/${cleanEncode(state.projectMetadata.id)}`);
    return res.data?.MESSAGE || "InProgress"; // no itx
  } catch {
    return "InProgress"; // no itx
  }
}

export async function downloadEtsProject() {
  try {
    state.projectMetadata.etsPin = etsPin ?? state.projectMetadata.etsPin;
    const { id, name } = state.projectMetadata;
    const summary = await downloadEtsProjectSummary(id);
    const res = await axios.get(`/api/projectexport/downloadProjectData/${cleanEncode(id)}`, {
      responseType: "blob",
    });
    const blob = res.data;
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${name}.knxproj`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    resetExportState();
    universalstorageHandler.saveProjectMetaData(state.projectMetadata);
    await deleteEtsProjectFromQueue(id);
    await deleteEtsProjectSummaryFromQueue(id);

    if (summary.length > 0) {
      await showETSSyncStatsWithCatalogIssues(summary, "export");
    }
  } catch (err) {
    logger.error(err);
  }
}

async function downloadEtsProjectSummary(id: string): Promise<any[]> {
  try {
    const res = await axios.get(`/api/projectexport/downloadXchangeData/${cleanEncode(getEtsProjectSummaryId(id))}`, {
      responseType: "text",
    });
    const data = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
    const idMappings = Array.isArray(data?.idMappings) ? data.idMappings : Array.isArray(data) ? data : [];
    return idMappings.filter((item: any) => item?.deviceId != null);
  } catch (err: any) {
    if (err?.response?.status !== 404) {
      logger.error(err);
    }
    return [];
  }
}

async function deleteEtsProjectSummaryFromQueue(id: string): Promise<boolean> {
  try {
    const res = await axios.post(`/api/projectexport/deleteXchangeData/${cleanEncode(getEtsProjectSummaryId(id))}`);
    return res.data?.STATUS === "SUCCESS" || res.data?.DATA?.code === "BlobNotFound";
  } catch (err: any) {
    if (err?.response?.status !== 404) {
      logger.error(err);
    }
    return false;
  }
}

function getEtsProjectSummaryId(id: string): string {
  return `${id}${ETS_PROJECT_SUMMARY_SUFFIX}`;
}

export async function deleteEtsProjectFromQueue(id: string) {
  try {
    const res = await axios.post(`/api/projectexport/deleteProjectData/${cleanEncode(id)}`);
    return res.data?.STATUS === "SUCCESS" || res.data?.DATA?.code === "BlobNotFound";
  } catch (err) {
    logger.error(err);
    return false;
  }
}

// ---------------- Helper Functions ----------------

export function resetExportState() {
  state.projectMetadata.exportState = ExportState.None;
  state.projectMetadata.raiseOnChange("exportState");
}
